import React, { useEffect, useRef } from 'react';

interface AnimatedCoinCounterProps {
  value: number; // Authoritative accrued/claimable amount
  ratePerSecond?: number; // Rate per second for smooth sub-second ticking
  decimals?: number; // Precision (default 4 for live mining ticker)
  className?: string;
  prefix?: string;
  suffix?: string;
}

export const AnimatedCoinCounter: React.FC<AnimatedCoinCounterProps> = ({
  value,
  ratePerSecond = 0,
  decimals = 4,
  className = '',
  prefix = '',
  suffix = '',
}) => {
  const spanRef = useRef<HTMLSpanElement>(null);
  const currentValRef = useRef<number>(value);
  const targetValRef = useRef<number>(value);
  const lastIntegerRef = useRef<number>(Math.floor(value));
  const lastTimeRef = useRef<number>(performance.now());
  const rafIdRef = useRef<number | null>(null);
  const pulseTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Synchronize target value whenever authoritative prop updates
  useEffect(() => {
    targetValRef.current = value;

    // If authoritative value dropped (e.g. claim completed), snap immediately
    if (value < currentValRef.current) {
      currentValRef.current = value;
      lastIntegerRef.current = Math.floor(value);
      if (spanRef.current) {
        spanRef.current.textContent = formatDisplay(value, decimals, prefix, suffix);
      }
    }
  }, [value, decimals, prefix, suffix]);

  useEffect(() => {
    // Check prefers-reduced-motion
    const mediaQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (mediaQuery?.matches) {
      if (spanRef.current) {
        spanRef.current.textContent = formatDisplay(value, decimals, prefix, suffix);
      }
      return;
    }

    lastTimeRef.current = performance.now();
    lastIntegerRef.current = Math.floor(currentValRef.current);

    const tick = (now: number) => {
      // If document became hidden, do not schedule next frame
      if (document.hidden) {
        rafIdRef.current = null;
        return;
      }

      const dt = Math.max(0, (now - lastTimeRef.current) / 1000);
      lastTimeRef.current = now;

      // Advance by active rate per second if positive
      if (ratePerSecond > 0 && targetValRef.current >= currentValRef.current) {
        targetValRef.current += dt * ratePerSecond;
      }

      // Smooth exponential approach toward target value
      const diff = targetValRef.current - currentValRef.current;
      if (Math.abs(diff) > 0.0000001) {
        const step = diff * Math.min(1, dt * 14);
        currentValRef.current += step;
      } else {
        currentValRef.current = targetValRef.current;
      }

      // Detect integer milestone increase for VIP gold glow pulse
      const currentInt = Math.floor(currentValRef.current);
      if (currentInt > lastIntegerRef.current) {
        lastIntegerRef.current = currentInt;
        if (spanRef.current) {
          spanRef.current.classList.remove('coin-counter-pulse');
          // Force layout reflow so the CSS animation restarts
          void spanRef.current.offsetWidth;
          spanRef.current.classList.add('coin-counter-pulse');

          if (pulseTimerRef.current) {
            clearTimeout(pulseTimerRef.current);
          }
          pulseTimerRef.current = setTimeout(() => {
            if (spanRef.current) {
              spanRef.current.classList.remove('coin-counter-pulse');
            }
          }, 460);
        }
      }

      // Update DOM element directly for optimal 60fps performance without React re-renders
      if (spanRef.current) {
        spanRef.current.textContent = formatDisplay(currentValRef.current, decimals, prefix, suffix);
      }

      rafIdRef.current = requestAnimationFrame(tick);
    };

    // Start ticker loop only if visible
    if (!document.hidden) {
      rafIdRef.current = requestAnimationFrame(tick);
    }

    // Reconcile after tab visibility changes (e.g., returning from Telegram chat or background)
    const handleVisibility = () => {
      if (document.hidden) {
        // Strict battery & GPU optimization: kill RAF loop while backgrounded
        if (rafIdRef.current) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
      } else {
        // App resumed / foregrounded: reset timestamp and snap to latest target to avoid visual jump
        lastTimeRef.current = performance.now();
        currentValRef.current = targetValRef.current;
        lastIntegerRef.current = Math.floor(currentValRef.current);
        if (spanRef.current) {
          spanRef.current.textContent = formatDisplay(currentValRef.current, decimals, prefix, suffix);
        }
        if (!rafIdRef.current) {
          rafIdRef.current = requestAnimationFrame(tick);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      if (pulseTimerRef.current) {
        clearTimeout(pulseTimerRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [ratePerSecond, decimals, prefix, suffix, value]);

  return (
    <span
      ref={spanRef}
      className={`inline-block font-mono tabular-nums select-none ${className}`}
      style={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {formatDisplay(value, decimals, prefix, suffix)}
    </span>
  );
};

function formatDisplay(val: number, dec: number, pre: string, suf: string): string {
  const safeVal = Math.max(0, isNaN(val) ? 0 : val);
  const formattedNumber = safeVal.toLocaleString(undefined, {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  });
  return `${pre}${formattedNumber}${suf ? ` ${suf}` : ''}`;
}
